<script>
  // Config-driven data table — the canonical way to render list/index pages.
  //
  // Pagination, filtering and sorting stay SERVER-side (Pagy + URL params). This
  // component owns look-and-feel only: consistent headers, alignment, skeleton
  // loading, empty state, optional server-sort headers, and drag reorder.
  //
  //   const columns = [
  //     { key: 'name', header: 'Name', field: 'name', sortKey: 'name' },
  //     { key: 'points', header: 'Points', field: 'points', align: 'right' },
  //     { key: 'status', header: 'Status' },              // custom -> cell snippet
  //   ]
  //
  //   <DataGrid {columns} rows={quests} {loading} sort={{ key, dir }} onsort={go}>
  //     {#snippet cell(row, column)}
  //       {#if column.key === 'status'}<StateChip state={row.state} />{/if}
  //     {/snippet}
  //     {#snippet actions(row)}
  //       <Button variant="ghost" size="xs" square icon="edit" ... />
  //     {/snippet}
  //   </DataGrid>
  import { dndzone, SOURCES, TRIGGERS } from 'svelte-dnd-action'
  import { flip } from 'svelte/animate'
  import Icon from './icon.svelte'
  import EmptyState from '../empty_state.svelte'
  import { transformDraggedRow } from '../../lib/table_drag.js'

  const FLIP_DURATION_MS = 150

  let {
    columns = [],
    rows = [],
    loading = false,
    emptyMessage = 'No records found.',
    emptyIcon = null,
    rowKey = (row) => row.id,
    size = 'md',
    zebra = false,
    dragEnabled = false,
    onreorder,
    sort = null,
    onsort,
    cell,
    actions,
    empty
  } = $props()

  let localRows = $state(rows)
  $effect(() => {
    localRows = rows
  })

  let tbodyEl = $state(null)

  // Reordering is uncommon, so rows are only draggable from the dedicated
  // handle column (svelte-dnd-action's documented handle pattern): dragging
  // stays disabled until pointer-down on a handle, and re-disables on drop.
  let handleHeld = $state(false)
  const dragActive = $derived(dragEnabled && handleHeld)

  const startDrag = (event) => {
    event.preventDefault()
    handleHeld = true
  }
  const handleKeyDown = (event) => {
    if ((event.key === 'Enter' || event.key === ' ') && !handleHeld) handleHeld = true
  }

  const SIZES = { sm: 'table-sm', md: '', lg: 'table-lg' }
  const ALIGN = { left: 'text-left', center: 'text-center', right: 'text-right' }

  const colCount = $derived(columns.length + (dragEnabled ? 1 : 0) + (actions ? 1 : 0))
  const tableClasses = $derived(['table', SIZES[size] ?? '', zebra ? 'table-zebra' : ''].filter(Boolean).join(' '))

  const resolve = (row, field) =>
    typeof field === 'function' ? field(row) : field.split('.').reduce((acc, k) => acc?.[k], row)

  const handleConsider = (event) => {
    localRows = event.detail.items
    const { source, trigger } = event.detail.info
    if (source === SOURCES.KEYBOARD && trigger === TRIGGERS.DRAG_STOPPED) handleHeld = false
  }
  const handleFinalize = (event) => {
    localRows = event.detail.items
    if (event.detail.info.source === SOURCES.POINTER) handleHeld = false
    onreorder?.(localRows)
  }

  const sortIcon = (column) => {
    if (!sort || sort.key !== column.sortKey) return null
    return sort.dir === 'desc' ? 'chevron_down' : 'chevron_up'
  }
</script>

<div class="overflow-x-auto">
  <table class={tableClasses}>
    <thead>
      <tr>
        {#if dragEnabled}<th class="w-8 px-1"><span class="sr-only">Reorder</span></th>{/if}
        {#each columns as column (column.key)}
          <th class={[ALIGN[column.align] ?? '', column.headerClass ?? ''].filter(Boolean).join(' ')}>
            {#if column.sortKey && onsort}
              <button
                type="button"
                class="inline-flex items-center gap-1 hover:text-base-content"
                onclick={() => onsort(column.sortKey)}
              >
                {column.header}
                {#if sortIcon(column)}<Icon name={sortIcon(column)} size="xs" />{/if}
              </button>
            {:else}
              {column.header}
            {/if}
          </th>
        {/each}
        {#if actions}
          <th class="text-right sticky right-0 bg-base-100">Actions</th>
        {/if}
      </tr>
    </thead>

    <tbody
      bind:this={tbodyEl}
      use:dndzone={{
        items: localRows,
        dragDisabled: !dragActive,
        flipDurationMs: FLIP_DURATION_MS,
        dropTargetStyle: {},
        transformDraggedElement: transformDraggedRow(() => tbodyEl)
      }}
      onconsider={handleConsider}
      onfinalize={handleFinalize}
    >
      {#if loading}
        {#each Array(5) as _, index (index)}
          <tr>
            <td colspan={colCount}><div class="skeleton h-4 w-full"></div></td>
          </tr>
        {/each}
      {:else if localRows.length === 0}
        {#if empty}
          {@render empty()}
        {:else if emptyIcon}
          <tr>
            <td colspan={colCount}><EmptyState icon={emptyIcon} title={emptyMessage} /></td>
          </tr>
        {:else}
          <tr>
            <td colspan={colCount} class="text-center text-base-content/60 py-8">{emptyMessage}</td>
          </tr>
        {/if}
      {:else}
        {#each localRows as row, index (rowKey(row))}
          <tr class="hover:bg-base-200/40 transition-colors" animate:flip={{ duration: FLIP_DURATION_MS }}>
            {#if dragEnabled}
              <td class="w-8 px-1">
                <button
                  type="button"
                  class="cursor-grab touch-none text-base-content/30 hover:text-base-content/70 align-middle"
                  aria-label="Drag to reorder"
                  onmousedowncapture={startDrag}
                  ontouchstartcapture={startDrag}
                  onmouseup={() => (handleHeld = false)}
                  onkeydown={handleKeyDown}
                >
                  <Icon name="drag_handle" size="sm" />
                </button>
              </td>
            {/if}
            {#each columns as column (column.key)}
              <td class={[ALIGN[column.align] ?? '', column.cellClass ?? ''].filter(Boolean).join(' ')}>
                {#if cell && !column.field}
                  {@render cell(row, column, index)}
                {:else if column.field}
                  {resolve(row, column.field)}
                {:else if cell}
                  {@render cell(row, column, index)}
                {/if}
              </td>
            {/each}
            {#if actions}
              <!-- Pinned right: a wide table (users, with up to ten row actions) keeps them on screen. -->
              <td class="text-right sticky right-0 bg-base-100">
                <div class="flex justify-end gap-1 whitespace-nowrap">{@render actions(row, index)}</div>
              </td>
            {/if}
          </tr>
        {/each}
      {/if}
    </tbody>
  </table>
</div>
